import React from 'react';
import { motion } from 'framer-motion';

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  description,
  size = 'md',
}) => {
  const sizes = {
    sm: { track: 'w-8 h-4', thumb: 'w-3 h-3', translate: 16 },
    md: { track: 'w-11 h-6', thumb: 'w-5 h-5', translate: 20 },
    lg: { track: 'w-14 h-7', thumb: 'w-6 h-6', translate: 28 },
  };

  const { track, thumb, translate } = sizes[size];

  const toggle = (
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only peer"
      />
      <motion.div
        className={`${track} rounded-full ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !checked ? 'bg-zinc-300' : ''
        }`}
        style={{
          backgroundColor: checked ? 'var(--accent)' : undefined
        }}
      />
      <motion.div
        animate={{ x: checked ? translate : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`absolute top-0.5 left-0.5 ${thumb} bg-white rounded-full shadow`}
      />
    </div>
  );

  if (!label && !description) {
    return (
      <label className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
        {toggle}
      </label>
    );
  }

  return (
    <motion.label
      className={`flex items-start gap-4 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} group`}
      whileHover={disabled ? {} : { x: 2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <div className="mt-0.5">
        {toggle}
      </div>
      <div className="flex-1">
        {label && (
          <p className="text-sm font-medium text-primary group-hover:text-accent transition-colors">
            {label}
          </p>
        )}
        {description && (
          <p className="text-sm text-muted">{description}</p>
        )}
      </div>
    </motion.label>
  );
};

export default Toggle;
